import React from "react";
import type { ReactElement } from "react";

import { Button2 } from "@/ui2/atoms/Button";
import EnvelopeIcon from "@/assets/images/icons2/icon--envelope.svg?component";
import { formatMobileNumber } from "@/utils/formInputUtils";
import type { PatientDetails } from "@/core/types";
import PhoneIcon from "@/assets/images/icons2/icon--phone.svg?component";
import { Tooltip2 } from "@/ui2/molecules/Tooltip";
import { useCopyToClipboard } from "@/lib/hooks/useCopyToClipboard";
import VerifiedIcon from "@/assets/images/icons2/icon--verified.svg?component";

type Props = {
  patientDetails: Pick<PatientDetails, "email" | "email_verified" | "mobile" | "preferences">;
};

export function PatientContact({ patientDetails }: Props) {
  const copy = useCopyToClipboard();

  return (
    <ul className="tw:flex tw:flex-col tw:items-start">
      <ContactDetail
        isVerified={patientDetails.preferences?.is_sms_verified}
        verifiedLabel="Verified phone number"
      >
        <ContactDetailPressable
          icon={<PhoneIcon />}
          onPress={() => copy(patientDetails.mobile)}
          text={formatMobileNumber(patientDetails.mobile)}
        />
      </ContactDetail>

      <ContactDetail
        isVerified={patientDetails.email_verified}
        verifiedLabel="Verified email address"
      >
        <ContactDetailPressable
          icon={<EnvelopeIcon />}
          onPress={() => copy(patientDetails.email)}
          text={patientDetails.email}
        />
      </ContactDetail>
    </ul>
  );
}

function ContactDetail({
  children,
  isVerified,
  verifiedLabel,
}: {
  children: React.ReactNode;
  isVerified: boolean;
  verifiedLabel: string;
}) {
  return (
    <li className="tw:flex tw:w-full tw:items-center tw:gap-2 tw:border-t-stroke-quaternary tw:py-2 tw:not-first:border-t">
      {children}

      {isVerified && (
        <Tooltip2 text={verifiedLabel}>
          <VerifiedIcon className="tw:text-text-success-secondary" />
        </Tooltip2>
      )}
    </li>
  );
}

function ContactDetailPressable({
  icon,
  onPress,
  text,
}: {
  icon: ReactElement;
  onPress: () => void;
  text: string;
}) {
  return (
    <Button2
      classNameChildren="tw:text-left tw:font-normal tw:break-all tw:whitespace-normal"
      data-private
      icon={icon}
      onPress={onPress}
      size="sm"
      variant="link"
    >
      {text}
    </Button2>
  );
}
